import { useQuery, useQueryClient } from '@tanstack/react-query'

import { apiFetch } from '@/lib/api'

/*
 * O catálogo de camadas, como `GET /api/v1/camadas` o devolve: o que o painel
 * lista e o que o mapa sabe pintar. Nome, ordem, entrega e as faixas são dado
 * do servidor — a entrega de uma variável muda `entregue` sem build novo —, e
 * o frontend só decide o estilo de cada grupo.
 */

/**
 * Os grupos que o mapa sabe pintar: o VC, as variáveis, os limites do CAR, as
 * referências e as camadas da gestão (desenhos que a equipe do observatório
 * publicou, cada um na própria cor).
 */
const GRUPOS = ['valor', 'variavel', 'limites', 'referencia', 'gestao'] as const
export type GrupoDaCamada = (typeof GRUPOS)[number]

export interface FaixaDoCatalogo {
  codigo: number
  nome: string
  limiteInferior: number
  limiteSuperior: number
}

export interface SubcamadaDoCatalogo {
  codigo: number
  nome: string
}

export interface CamadaDoCatalogo {
  codigo: string
  nome: string
  grupo: GrupoDaCamada
  /** Falso para a camada que o IIS ainda não entregou: ela consta, mas não desenha nada. */
  entregue: boolean
  ordem: number
  /** A camada no GeoServer, como `gef:imovel`. */
  camadaServidor: string
  /** O atributo do tile que pinta a camada; nulo nas referências. */
  atributo: string | null
  faixas: FaixaDoCatalogo[]
  subcamadas: SubcamadaDoCatalogo[]
  /**
   * A cor do desenho, `#RRGGBB`, que é estilo do mapa: a camada da gestão tem
   * sempre; a variável temática, depois que a gestão a edita ou publica.
   */
  cor: string | null
  /**
   * O texto do ícone *i*: a camada da gestão o traz sempre, e a variável
   * temática, depois que a gestão o escreve. Sem ele, vale a lista de textos
   * do portal.
   */
  ajuda: string | null
  /** O `camada_id` das feições dela no tile; só o que a gestão desenha tem. */
  identificador: number | null
}

/** A camada do servidor de mapas que traz, num tile só, todos os desenhos que a gestão publicou. */
export const CAMADA_DA_GESTAO_NO_SERVIDOR = 'gef:camada_gestao'

/**
 * A camada é um desenho da gestão, na cor única dela: a camada da gestão e a
 * variável temática cujo arquivo a gestão incluiu e publicou. A variável
 * continua no grupo e na posição dela no painel, mas se desenha, se legenda e
 * se separa no tile como a camada da gestão — e não pelas faixas do valor.
 */
export function desenhadaPelaGestao(
  camada: Pick<CamadaDoCatalogo, 'camadaServidor' | 'cor' | 'identificador'>
): boolean {
  return (
    camada.camadaServidor === CAMADA_DA_GESTAO_NO_SERVIDOR &&
    camada.cor !== null &&
    camada.identificador !== null
  )
}

/** A resposta do catálogo não tem a forma combinada com a API. */
export class CatalogoInvalido extends Error {
  constructor(motivo: string) {
    super(`Catálogo de camadas fora da forma: ${motivo}`)
    this.name = 'CatalogoInvalido'
  }
}

function objeto(valor: unknown, onde: string): Record<string, unknown> {
  if (typeof valor !== 'object' || valor === null || Array.isArray(valor)) {
    throw new CatalogoInvalido(`${onde} não é objeto`)
  }
  return valor as Record<string, unknown>
}

function texto(valor: unknown, onde: string): string {
  if (typeof valor !== 'string') throw new CatalogoInvalido(`${onde} não é texto`)
  return valor
}

function numero(valor: unknown, onde: string): number {
  if (typeof valor !== 'number' || !Number.isFinite(valor)) {
    throw new CatalogoInvalido(`${onde} não é número`)
  }
  return valor
}

function lista(valor: unknown, onde: string): unknown[] {
  if (!Array.isArray(valor)) throw new CatalogoInvalido(`${onde} não é lista`)
  return valor
}

function lerFaixa(valor: unknown, onde: string): FaixaDoCatalogo {
  const faixa = objeto(valor, onde)
  return {
    codigo: numero(faixa.codigo, `${onde}.codigo`),
    nome: texto(faixa.nome, `${onde}.nome`),
    limiteInferior: numero(faixa.limiteInferior, `${onde}.limiteInferior`),
    limiteSuperior: numero(faixa.limiteSuperior, `${onde}.limiteSuperior`),
  }
}

function lerSubcamada(valor: unknown, onde: string): SubcamadaDoCatalogo {
  const sub = objeto(valor, onde)
  return { codigo: numero(sub.codigo, `${onde}.codigo`), nome: texto(sub.nome, `${onde}.nome`) }
}

function textoOuNulo(valor: unknown, onde: string): string | null {
  return valor === undefined || valor === null ? null : texto(valor, onde)
}

function numeroOuNulo(valor: unknown, onde: string): number | null {
  return valor === undefined || valor === null ? null : numero(valor, onde)
}

const COR_HEXADECIMAL = /^#[0-9A-Fa-f]{6}$/

function ehGrupo(valor: string): valor is GrupoDaCamada {
  return (GRUPOS as readonly string[]).includes(valor)
}

/**
 * Confere a resposta e a traduz em camadas, na ordem do catálogo. Camada de
 * grupo que esta versão não conhece fica de fora: o mapa não saberia pintá-la,
 * e uma caixa que não faz nada seria pior que nenhuma. Qualquer outro desvio
 * da forma é erro — e o painel o mostra, em vez de uma lista pela metade.
 */
export function lerCatalogo(corpo: unknown): CamadaDoCatalogo[] {
  const camadas = lista(objeto(corpo, 'a resposta').camadas, 'camadas')
  const lidas = camadas
    .map((valor, indice): CamadaDoCatalogo | null => {
      const onde = `camadas[${indice}]`
      const camada = objeto(valor, onde)
      const grupo = texto(camada.grupo, `${onde}.grupo`)
      if (!ehGrupo(grupo)) return null
      if (typeof camada.entregue !== 'boolean') {
        throw new CatalogoInvalido(`${onde}.entregue não é sim ou não`)
      }
      const cor = textoOuNulo(camada.cor, `${onde}.cor`)
      const ajuda = textoOuNulo(camada.ajuda, `${onde}.ajuda`)
      const identificador = numeroOuNulo(camada.identificador, `${onde}.identificador`)
      const camadaServidor = texto(camada.camadaServidor, `${onde}.camadaServidor`)
      // A variável que a gestão publicou vem do tile da gestão: sem a cor ou o
      // número da camada, como na camada da gestão, não há como pintá-la nem
      // separar o desenho dela. O texto pode faltar: o *i* usa o do portal.
      const desenhoDaGestao =
        grupo === 'gestao' || (camadaServidor === CAMADA_DA_GESTAO_NO_SERVIDOR && camada.entregue)
      if (desenhoDaGestao) {
        // Sem cor, sem texto ou sem identificador não há como pintar nem filtrar
        // o desenho: a resposta está fora da forma, e o painel o diz.
        if (cor === null || !COR_HEXADECIMAL.test(cor)) {
          throw new CatalogoInvalido(`${onde}.cor não é #RRGGBB`)
        }
        if (grupo === 'gestao' && ajuda === null) {
          throw new CatalogoInvalido(`${onde}.ajuda ausente`)
        }
        if (identificador === null) throw new CatalogoInvalido(`${onde}.identificador ausente`)
      }
      return {
        codigo: texto(camada.codigo, `${onde}.codigo`),
        nome: texto(camada.nome, `${onde}.nome`),
        grupo,
        entregue: camada.entregue,
        ordem: numero(camada.ordem, `${onde}.ordem`),
        camadaServidor,
        atributo: camada.atributo === null ? null : texto(camada.atributo, `${onde}.atributo`),
        faixas: lista(camada.faixas, `${onde}.faixas`).map((f, i) =>
          lerFaixa(f, `${onde}.faixas[${i}]`)
        ),
        subcamadas: lista(camada.subcamadas, `${onde}.subcamadas`).map((s, i) =>
          lerSubcamada(s, `${onde}.subcamadas[${i}]`)
        ),
        cor,
        ajuda,
        identificador,
      }
    })
    .filter((camada) => camada !== null)
    .sort((a, b) => a.ordem - b.ordem)
  return gestaoDepoisDeBiomas(lidas)
}

/**
 * A API põe as camadas da gestão no fim do catálogo, mas o painel as lista
 * logo depois de Biomas, entre as camadas temáticas e antes das malhas de
 * estados e municípios. Sem Biomas no catálogo, elas ficam onde a API as pôs.
 */
function gestaoDepoisDeBiomas(camadas: CamadaDoCatalogo[]): CamadaDoCatalogo[] {
  const daGestao = camadas.filter((camada) => camada.grupo === 'gestao')
  const demais = camadas.filter((camada) => camada.grupo !== 'gestao')
  const biomas = demais.findIndex((camada) => camada.codigo === 'biomas')
  if (daGestao.length === 0 || biomas === -1) return camadas
  return [...demais.slice(0, biomas + 1), ...daGestao, ...demais.slice(biomas + 1)]
}

/** O catálogo de camadas, pedido uma vez e compartilhado pelo painel fixo e pela gaveta. */
export function useCatalogo() {
  const retentarPadrao = useQueryClient().getDefaultOptions().queries?.retry
  return useQuery({
    queryKey: ['camadas'],
    // Resposta fora da forma voltaria igual: retentar só atrasaria o aviso.
    // As demais falhas seguem a regra da aplicação.
    retry: (tentativa, erro) => {
      if (erro instanceof CatalogoInvalido) return false
      if (typeof retentarPadrao === 'function') return retentarPadrao(tentativa, erro)
      if (typeof retentarPadrao === 'number') return tentativa < retentarPadrao
      return retentarPadrao ?? false
    },
    queryFn: async () => lerCatalogo(await apiFetch<unknown>('/v1/camadas')),
  })
}
